import { useCallback, useEffect, useMemo, useState } from 'react';
import { api, ApiError, mailinblackUrl, type CachedSpool, type Category, type StoreStats } from './lib/api.js';
import {
  formatDate,
  readOverrides,
  resolveMailboxes,
  useFrontContext,
  writeOverrides,
} from './lib/front.js';
import { SpoolList } from './components/SpoolList.js';
import { MailboxSettings } from './components/MailboxSettings.js';
import { App as Shell, Banner, Button, Section, Spinner } from './components/ui.js';

/** Onglets de l'interface web Mailinblack, dans le meme ordre. */
const TABS: Array<{ key: Category; short: string; label: string }> = [
  { key: 'attente', short: 'Attente', label: 'En attente' },
  { key: 'newsletter', short: 'News.', label: 'Newsletters' },
  { key: 'spearphishing', short: 'Spear.', label: 'Spearphishing' },
  { key: 'banni', short: 'Bannis', label: 'Bannis' },
  { key: 'spam', short: 'Spams', label: 'Spams' },
  { key: 'infecte', short: 'Infectés', label: 'Infectés' },
];

const EMPTY_LABEL: Record<Category, string> = {
  attente: 'Aucun message en attente de décision.',
  newsletter: 'Aucune newsletter retenue.',
  spearphishing: 'Aucune tentative de spearphishing.',
  banni: 'Aucun expéditeur banni.',
  spam: 'Aucun spam retenu.',
  infecte: 'Aucun message infecté.',
  delivre: 'Aucun message délivré.',
  autre: 'Rien à afficher.',
};

/**
 * Panneau lateral : la quarantaine du collaborateur connecte.
 *
 * Volontairement independant de la conversation ouverte dans Front — c'est une
 * vue « ma zone Mailinblack », consultable a tout moment. Les onglets reprennent
 * ceux de l'interface web de Mailinblack.
 *
 * Aucun titre ni filtre de date : le panneau est deja identifie par son onglet
 * dans Front, et Mailinblack ne conserve qu'environ un mois de quarantaine. La
 * hauteur gagnee sert a afficher davantage de messages sans defilement.
 */
export function App() {
  const context = useFrontContext();

  const [overrides, setOverrides] = useState<string[]>(() => readOverrides());
  const [active, setActive] = useState<string | null>(null);
  const [items, setItems] = useState<CachedSpool[]>([]);
  const [counts, setCounts] = useState<Record<Category, number> | null>(null);
  const [stats, setStats] = useState<StoreStats | null>(null);
  const [canAct, setCanAct] = useState(false);
  const [tab, setTab] = useState<Category>('attente');
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const detected = context?.teammate?.email ?? null;

  /** Adresses declarees par l'utilisateur, ou a defaut celle de son compte Front. */
  const available = useMemo(
    () => resolveMailboxes(context?.teammate, overrides),
    [context?.teammate, overrides],
  );

  // L'adresse courante suit la liste : si elle en disparait, on retombe sur la premiere.
  useEffect(() => {
    setActive((current) => (current && available.includes(current) ? current : (available[0] ?? null)));
  }, [available]);

  useEffect(() => {
    api
      .capabilities()
      .then((result) => setCanAct(result.canAct))
      .catch(() => setCanAct(false));
  }, []);

  const load = useCallback(async (mailbox: string | null, category: Category) => {
    if (!mailbox) {
      setItems([]);
      setCounts(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const result = await api.mine([mailbox], category);
      setItems(result.items);
      setCounts(result.counts);
      setStats(result.stats);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : (cause as Error).message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load(active, tab);
  }, [active, tab, load]);

  const act = useCallback(
    async (spool: CachedSpool, kind: 'retrieve' | 'authorize') => {
      setBusyId(spool.id);
      setError(null);
      try {
        if (kind === 'retrieve') await api.retrieve([spool.id]);
        else await api.authorize([spool.id]);

        // Le serveur a deja purge le message de son cache : on reflete
        // immediatement le retrait plutot que de relancer une recherche.
        setItems((current) => current.filter((item) => item.id !== spool.id));
        setCounts((current) =>
          current ? { ...current, [tab]: Math.max(0, current[tab] - 1) } : current,
        );
        setNotice(
          kind === 'retrieve'
            ? 'Message délivré dans votre boîte.'
            : `Expéditeur autorisé : ${spool.sender}.`,
        );
      } catch (cause) {
        setError(cause instanceof ApiError ? cause.message : (cause as Error).message);
      } finally {
        setBusyId(null);
      }
    },
    [tab],
  );

  const staleness = useMemo(() => {
    if (!stats?.lastSyncAt) return null;
    const minutes = Math.round((Date.now() - stats.lastSyncAt) / 60_000);
    return minutes <= 1 ? "à l'instant" : `il y a ${minutes} min`;
  }, [stats]);

  if (!context) {
    return (
      <Shell>
        <Spinner label="Connexion à Front…" />
      </Shell>
    );
  }

  const mibUrl = mailinblackUrl();
  const currentTab = TABS.find((entry) => entry.key === tab);

  return (
    <Shell>
      {/* Barre d'identité : boîte courante à gauche, réglage à droite. */}
      <div className="toolbar">
        {available.length > 1 ? (
          <select
            className="mailbox-select"
            value={active ?? ''}
            onChange={(event) => setActive(event.target.value)}
            title="Boîte affichée"
          >
            {available.map((address) => (
              <option key={address} value={address}>
                {address}
              </option>
            ))}
          </select>
        ) : (
          <span className="mailbox-fixed">{active ?? 'aucune adresse'}</span>
        )}

        <Button onClick={() => setSettingsOpen(!settingsOpen)} title="Choisir les adresses surveillées">
          Mes adresses
        </Button>
      </div>

      {settingsOpen ? (
        <MailboxSettings
          current={overrides}
          detected={detected}
          onSave={(addresses) => {
            writeOverrides(addresses);
            setOverrides(addresses);
            setSettingsOpen(false);
            setNotice(addresses.length > 0 ? 'Adresses enregistrées.' : 'Retour à votre adresse Front.');
          }}
          onClose={() => setSettingsOpen(false)}
        />
      ) : (
        <Section
          title={currentTab?.label ?? 'Quarantaine'}
          aside={<span className="muted">{items.length}</span>}
        >
          <nav className="tabs" role="tablist">
            {TABS.map((entry) => (
              <button
                key={entry.key}
                type="button"
                role="tab"
                aria-selected={tab === entry.key}
                title={entry.label}
                className={`tab tab-${entry.key}${tab === entry.key ? ' tab-active' : ''}`}
                onClick={() => setTab(entry.key)}
              >
                {entry.short}
                {counts && counts[entry.key] > 0 && <span className="tab-count">{counts[entry.key]}</span>}
              </button>
            ))}
          </nav>

          {error && <Banner tone="error">{error}</Banner>}
          {notice && <Banner tone="success">{notice}</Banner>}

          {available.length === 0 && !loading && (
            <Banner tone="warning">
              Votre adresse n’a pas pu être déterminée depuis Front. Renseignez-la via « Mes adresses ».
            </Banner>
          )}

          {loading && <Spinner label="Lecture de la quarantaine…" />}

          {!loading && active && items.length === 0 && !error && (
            <Banner tone="empty">{EMPTY_LABEL[tab]}</Banner>
          )}

          {!loading && items.length > 0 && (
            <SpoolList
              items={items}
              canAct={canAct}
              busyId={busyId}
              onRetrieve={(spool) => void act(spool, 'retrieve')}
              onAuthorize={(spool) => void act(spool, 'authorize')}
            />
          )}

          {!canAct && items.length > 0 && (
            <Banner tone="warning">
              Consultation seule : aucune clé Mailinblack en écriture n’est configurée sur le serveur.
            </Banner>
          )}
        </Section>
      )}

      <footer className="footer">
        <span
          title={
            stats
              ? `Cache : ${stats.size} messages, du ${formatDate(stats.oldestAt)} au ${formatDate(stats.newestAt)}`
              : undefined
          }
        >
          {items.length} message{items.length > 1 ? 's' : ''}
          {staleness ? ` · ${staleness}` : ''}
        </span>
        <span className="footer-links">
          <Button variant="link" onClick={() => void api.refresh().then(() => load(active, tab))}>
            Actualiser
          </Button>
          {mibUrl && context.openUrl && (
            <Button variant="link" onClick={() => context.openUrl?.(mibUrl)}>
              Mailinblack
            </Button>
          )}
        </span>
      </footer>
    </Shell>
  );
}
